import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Lock, Heart, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#C9A26B] transition-colors duration-300 text-sm";

const btnCls =
  "text-xs tracking-[0.3em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-8 py-4 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50";

const coverUrl = (code) => `${API}/api/galleries/${encodeURIComponent(code)}/cover`;

const coverStyle = (pos) => ({ objectPosition: `center ${pos ?? 50}%` });

const formatReadyDate = (d) => {
  if (!d) return null;
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const daysUntil = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return Math.ceil((dt.getTime() - Date.now()) / 86400000);
};

const PendingView = ({ code, gallery }) => {
  const ready = formatReadyDate(gallery.photos_ready_date);
  const days = gallery.photos_ready_date ? daysUntil(gallery.photos_ready_date) : null;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-4xl select-none" data-testid="gallery-pending" onContextMenu={(e) => e.preventDefault()}>
      {gallery.has_cover && (
        <div className="relative h-[52vh] min-h-[340px] overflow-hidden rounded-sm mb-14 -mt-8" data-testid="pending-wallpaper">
          <img src={coverUrl(code)} alt="Wedding wallpaper" draggable={false}
            className="w-full h-full object-cover pointer-events-none" style={coverStyle(gallery.cover_position)} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
      )}
      <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Being edited in the studio</p>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tighter text-[#F5F4F0] mb-4">{gallery.couple_name}</h1>
      {gallery.wedding_date && <p className="text-sm text-[#737373] mb-12">{gallery.wedding_date}</p>}
      {ready ? (
        <div className="border border-[#C9A26B]/40 p-10 mb-10 max-w-xl" data-testid="pending-ready-date">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#525252] mb-3">Your photos will be ready on</p>
          <p className="font-serif font-light text-3xl md:text-4xl tracking-tight text-[#C9A26B]">{ready}</p>
          {days !== null && days > 0 && (
            <p className="text-xs tracking-[0.25em] uppercase text-[#A3A3A3] mt-4">in {days} day{days === 1 ? "" : "s"}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-[#A3A3A3] leading-relaxed mb-10 max-w-xl" data-testid="pending-ready-date">
          The studio is still editing this story — check back soon.
        </p>
      )}
      <p className="text-sm text-[#737373] leading-relaxed max-w-xl" data-testid="pending-note">
        {gallery.direct
          ? "No email, no account — just come back on that date and enter the same key, and your photos will be waiting."
          : "You'll get an email the moment the studio releases the gallery."}
      </p>
    </motion.div>
  );
};

export const ForgotForm = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent)
    return (
      <p className="text-sm text-[#A3A3A3] leading-relaxed" data-testid="forgot-sent">
        If that email is registered, a reset link is on its way — check your inbox (and spam).
      </p>
    );

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-testid="forgot-form">
      <div>
        <label htmlFor="forgot-email" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Your email</label>
        <input id="forgot-email" type="email" required data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@love.com" className={inputCls} />
      </div>
      <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className={`self-start ${btnCls}`}>
        {busy ? "Sending…" : "Email Me a Reset Link"}
      </button>
    </form>
  );
};

const LoginForm = ({ onDone }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    if (res.ok) onDone();
    else setError(res.error);
    setBusy(false);
  };

  return (
    <div>
      {forgot ? (
        <div className="flex flex-col gap-8">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B]">Forgot password</p>
          <ForgotForm />
          <button onClick={() => setForgot(false)} data-testid="forgot-back-btn" className="self-start text-xs tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] transition-colors duration-300">
            Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-8" data-testid="gallery-login-form">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B]">Sign in</p>
          <div>
            <label htmlFor="gl-email" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Email</label>
            <input id="gl-email" type="email" required data-testid="gallery-login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@love.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="gl-pass" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Password</label>
            <input id="gl-pass" type="password" required data-testid="gallery-login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="gallery-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="gallery-login-btn" className={`self-start ${btnCls}`}>
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <button type="button" onClick={() => setForgot(true)} data-testid="forgot-open-btn" className="self-start text-xs tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] transition-colors duration-300">
            Forgot your password?
          </button>
          <a href="/#register" className="text-xs tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] transition-colors duration-300">
            New here? Register on the homepage
          </a>
        </form>
      )}
    </div>
  );
};

const CodeEntry = ({ onUnlock, compact = false }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const pre = new URLSearchParams(window.location.search).get("code");
    if (pre) setCode(pre.toUpperCase());
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onUnlock(code.trim().toUpperCase());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-8" data-testid="gallery-code-form">
      <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B]">{compact ? "Join with a code" : "Have a code?"}</p>
      <div>
        <label htmlFor="gallery-code" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Gallery code</label>
        <input id="gallery-code" required data-testid="gallery-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="E.g. ELENA-4821" className={`${inputCls} tracking-[0.2em] uppercase`} />
      </div>
      {error && <p className="text-sm text-red-400" data-testid="gallery-code-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="gallery-access-btn" className={`self-start flex items-center gap-3 ${btnCls}`}>
        <Lock className="w-3.5 h-3.5" /> {busy ? "Opening…" : "Open Gallery"}
      </button>
    </form>
  );
};

const WeddingPicker = ({ weddings, onOpen }) => (
  <div className="w-full max-w-5xl" data-testid="wedding-picker">
    <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Your weddings</p>
    <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tighter text-[#F5F4F0] mb-14">Choose a <span className="font-accent italic text-[#C9A26B]">story</span></h1>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {weddings.map((w) => (
        <button
          key={w.code}
          onClick={() => onOpen(w.code)}
          data-testid={`wedding-card-${w.code}`}
          className="group relative overflow-hidden rounded-sm border border-white/10 text-left h-64 flex flex-col justify-end p-8 hover:border-[#C9A26B]/50 transition-colors duration-500"
        >
          {w.has_cover && (
            <>
              <img src={coverUrl(w.code)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" style={coverStyle(w.cover_position)} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            </>
          )}
          <div className="relative">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A26B] mb-2">{w.role === "couple" ? "Your wedding" : "Guest"} · {w.photo_count} photos</p>
            <p className="font-serif text-3xl text-[#F5F4F0]">{w.couple_name}</p>
            {w.wedding_date && <p className="text-xs text-[#A3A3A3] mt-1">{w.wedding_date}</p>}
          </div>
        </button>
      ))}
    </div>
  </div>
);

const EventsBlock = ({ events, canEdit, onDelete }) => {
  if (!events?.length && !canEdit) return null;
  return (
    <div className="mb-16" data-testid="events-block">
      <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-8">The Day</p>
      {events?.length > 0 && (
        <div className="flex flex-col gap-8">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-start gap-6 border-l border-[#C9A26B]/40 pl-6" data-testid={`event-${ev.id}`}>
              <div className="flex-1">
                <p className="font-serif text-2xl text-[#F5F4F0]">{ev.title}</p>
                {(ev.event_date || ev.time) && (
                  <p className="text-xs tracking-[0.25em] uppercase text-[#C9A26B] mt-1">{[ev.event_date, ev.time].filter(Boolean).join(" · ")}</p>
                )}
                {ev.description && <p className="text-sm text-[#A3A3A3] leading-relaxed mt-3">{ev.description}</p>}
              </div>
              {canEdit && (
                <button onClick={() => onDelete(ev.id)} data-testid={`event-delete-${ev.id}`} aria-label="Remove event"
                  className="text-[#525252] hover:text-red-400 transition-colors duration-300 mt-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AddEventForm = ({ code, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", event_date: "", time: "", description: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/galleries/${code}/events`, form);
      onAdded(data);
      setForm({ title: "", event_date: "", time: "", description: "" });
      setOpen(false);
      toast.success("Event added — guests can see it now");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} data-testid="add-event-open" className={`flex items-center gap-2 mb-16 ${btnCls} bg-transparent !text-[#C9A26B]`}>
        <Plus className="w-4 h-4" /> Add Event
      </button>
    );

  return (
    <form onSubmit={submit} className="border border-white/10 p-8 mb-16 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="add-event-form">
      <div>
        <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Event name *</label>
        <input required data-testid="event-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ceremony, Reception, Mehndi…" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Date</label>
          <input type="date" data-testid="event-date-input" value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Time</label>
          <input type="time" data-testid="event-time-input" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className={inputCls} />
        </div>
      </div>
      <div className="sm:col-span-2">
        <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Describe this event for your photographer *</label>
        <textarea rows={3} required data-testid="event-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Dress code, venue, timeline, must-have moments — e.g. Black tie. Ceremony in the garden at 4pm, we want the veil shot at sunset…" className={`${inputCls} resize-none`} />
        <p className="text-xs text-[#525252] mt-3 leading-relaxed">This goes straight to your photographer and is also shown to your guests.</p>
      </div>
      <div className="sm:col-span-2 flex gap-4">
        <button type="submit" disabled={busy} data-testid="event-save-btn" className={btnCls}>{busy ? "Saving…" : "Save Event"}</button>
        <button type="button" onClick={() => setOpen(false)} data-testid="event-cancel-btn" className="text-xs tracking-[0.3em] uppercase text-[#737373] hover:text-[#F5F4F0] transition-colors duration-300">Cancel</button>
      </div>
    </form>
  );
};

const FaceRegister = ({ code, onDone }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/galleries/${code}/face`, fd);
      toast.success(`Found you in ${data.matched} of ${data.total} photos`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#C9A26B]/40 p-10 mb-16 max-w-xl" data-testid="face-register">
      <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Privacy Mode</p>
      <h3 className="font-serif font-light text-3xl tracking-tighter text-[#F5F4F0] mb-4">
        Find <span className="font-accent italic text-[#C9A26B]">yourself</span> in the photos
      </h3>
      <p className="text-sm text-[#A3A3A3] leading-relaxed mb-8">
        This gallery is face-protected: every guest sees only the photos they appear in.
        Upload one clear selfie — we'll match it against the gallery and show you yours.
      </p>
      <input type="file" accept="image/*" className="hidden" ref={fileRef} data-testid="face-file-input"
        onChange={(e) => submit(e.target.files[0])} />
      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="face-upload-btn" className={btnCls}>
        {busy ? "Matching your face…" : "Register My Face"}
      </button>
      {busy && <p className="text-xs text-[#737373] mt-4">Comparing against the gallery — this takes a moment per photo…</p>}
    </div>
  );
};

const GalleryView = ({ code, gallery, canEdit, onEventAdded, onEventDeleted, onFaceDone }) => {
  const [galleryData, setGalleryData] = useState(gallery);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => { setGalleryData(gallery); }, [gallery]);

  // Signed photo links expire after 15 minutes — quietly refresh them.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.post("/galleries/access", { code });
        setGalleryData((prev) => ({ ...prev, photos: data.photos, zip_url: data.zip_url }));
      } catch { /* retry on next cycle */ }
    }, 13 * 60 * 1000);
    return () => clearInterval(t);
  }, [code]);

  const toggleFav = async (photoId) => {
    try {
      const { data } = await api.post("/galleries/favorite", { code, photo_id: photoId });
      setFavorites((prev) => {
        const next = new Set(prev);
        if (data.favorited) {
          next.add(photoId);
          toast.success("Added to your favorites");
        } else next.delete(photoId);
        return next;
      });
    } catch (e) {
      toast.error("Could not save favorite");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-6xl select-none" data-testid="client-gallery"
      onContextMenu={(e) => e.preventDefault()}>
      {galleryData.released === false && <PendingView code={code} gallery={galleryData} />}
      {galleryData.released !== false && (
      <>
      {galleryData.has_cover && (
        <div className="relative h-[46vh] min-h-[320px] overflow-hidden rounded-sm mb-14 -mt-8" data-testid="gallery-wallpaper">
          <img src={coverUrl(code)} alt="Wedding wallpaper" draggable={false} className="w-full h-full object-cover pointer-events-none" style={coverStyle(galleryData.cover_position)} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Your Gallery</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tighter text-[#F5F4F0]">{galleryData.couple_name}</h1>
          {galleryData.wedding_date && <p className="text-sm text-[#737373] mt-3">{galleryData.wedding_date}</p>}
        </div>
        {galleryData.zip_url && (
          <a href={`${API}${galleryData.zip_url}`} data-testid="gallery-download-all"
            className={`flex items-center gap-3 ${btnCls}`}>
            <Download className="w-4 h-4" /> Download All ({galleryData.photos.length})
          </a>
        )}
      </div>

      {canEdit && <AddEventForm code={code} onAdded={onEventAdded} />}
      <EventsBlock events={galleryData.events} canEdit={canEdit} onDelete={onEventDeleted} />

      {galleryData.needs_face ? (
        <FaceRegister code={code} onDone={onFaceDone} />
      ) : galleryData.photos.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="gallery-empty">
          Your photos are being prepared — check back soon, or ask us on WhatsApp.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="gallery-grid">
          {galleryData.photos.map((p, i) => (
            <figure key={p.id} className="group relative overflow-hidden rounded-sm aspect-square bg-[#121212]" data-testid={`gallery-photo-${i}`}>
              <img src={`${API}${p.url}`} alt={p.filename} loading="lazy" draggable={false}
                className="w-full h-full object-cover pointer-events-none" style={{ WebkitUserDrag: "none" }} />
              <button onClick={() => toggleFav(p.id)} data-testid={`photo-fav-${i}`} aria-label="Favorite this photo"
                className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-[#0A0A0A]/50 backdrop-blur-sm flex items-center justify-center transition-transform duration-300 hover:scale-110">
                <Heart className={`w-5 h-5 transition-colors duration-300 ${favorites.has(p.id) ? "text-[#C9A26B] fill-[#C9A26B]" : "text-[#F5F4F0]"}`} />
              </button>
              <a href={`${API}${p.url}`} download={p.filename} data-testid={`photo-download-${i}`}
                className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                aria-label={`Download ${p.filename}`}>
                <span className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#F5F4F0] border border-white/40 px-5 py-3 hover:bg-[#C9A26B] hover:text-[#0A0A0A] hover:border-[#C9A26B] transition-colors duration-300">
                  <Download className="w-4 h-4" /> Download
                </span>
              </a>
            </figure>
          ))}
        </div>
      )}
      </>
      )}
    </motion.div>
  );
};

export default function ClientGallery() {
  const { user, logout } = useAuth();
  const [session, setSession] = useState(null);
  const [weddings, setWeddings] = useState(null);

  const handleLogout = async () => {
    await logout();
    setSession(null);
    setWeddings(null);
    toast.success("Signed out — see you soon");
  };

  const loadWeddings = useCallback(() => {
    if (!user) return;
    api.get("/my/weddings").then((r) => setWeddings(r.data)).catch(() => setWeddings([]));
  }, [user]);

  useEffect(() => {
    setWeddings(null);
    loadWeddings();
  }, [loadWeddings]);

  const openByCode = async (code) => {
    const { data } = await api.post("/galleries/access", { code });
    setSession({ code: data.code || code.trim().toUpperCase(), gallery: data });
  };

  const { token: routeToken } = useParams();
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (routeToken) {
      setTokenError(false);
      (async () => {
        try {
          const { data } = await api.post("/galleries/access", { token: routeToken });
          setSession({ code: data.code, gallery: data });
        } catch (err) {
          setTokenError(true);
          toast.error(formatApiError(err));
        }
      })();
      return;
    }
    const preCode = new URLSearchParams(window.location.search).get("code");
    if (preCode) {
      openByCode(preCode).catch((err) => toast.error(formatApiError(err)));
    }
  }, [routeToken]);

  const joinAndOpen = async (code) => {
    try {
      await api.post("/weddings/join", { code });
      loadWeddings();
    } catch (e) { /* code alone still opens the gallery */ }
    await openByCode(code);
  };

  const canEdit = !!(user && session && (weddings || []).some((w) => w.code === session.code && w.role === "couple"));

  let content;
  if (tokenError && !session) {
    content = (
      <div className="w-full max-w-lg text-center" data-testid="gallery-access-denied" role="alert">
        <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Private Gallery</p>
        <h1 className="font-serif font-light text-4xl tracking-tighter text-[#F5F4F0] mb-6">Access denied</h1>
        <p className="text-sm text-[#A3A3A3] leading-relaxed">
          This link is not valid. Ask the couple or the studio for the correct gallery link or code.
        </p>
      </div>
    );
  } else if (session) {
    content = (
      <GalleryView
        code={session.code}
        gallery={session.gallery}
        canEdit={canEdit}
        onFaceDone={async () => {
          const { data } = await api.post("/galleries/access", { code: session.code });
          setSession((s) => ({ ...s, gallery: data }));
        }}
        onEventAdded={(ev) => setSession((s) => ({ ...s, gallery: { ...s.gallery, events: [...(s.gallery.events || []), ev] } }))}
        onEventDeleted={async (eventId) => {
          try {
            await api.delete(`/galleries/${session.code}/events/${eventId}`);
            setSession((s) => ({ ...s, gallery: { ...s.gallery, events: s.gallery.events.filter((e) => e.id !== eventId) } }));
          } catch (e) {
            toast.error("Could not remove event");
          }
        }}
      />
    );
  } else if (user === null) {
    content = <p className="text-[#525252]" data-testid="gallery-loading">Loading…</p>;
  } else if (user) {
    content = (
      <div className="w-full max-w-5xl flex flex-col gap-16">
        {weddings === null ? (
          <p className="text-[#525252]" data-testid="gallery-loading">Loading…</p>
        ) : weddings.length > 0 ? (
          <WeddingPicker weddings={weddings} onOpen={openByCode} />
        ) : (
          <div data-testid="no-weddings">
            <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Welcome</p>
            <h1 className="font-serif font-light text-4xl tracking-tighter text-[#F5F4F0] mb-6">No weddings linked yet</h1>
            <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-md mb-10">
              If you're a couple, your wedding appears here automatically once we create it with your email.
              If you're a guest, enter the code the couple shared with you.
            </p>
            <CodeEntry compact onUnlock={joinAndOpen} />
          </div>
        )}
        {weddings && weddings.length > 0 && (
          <div className="border-t border-white/10 pt-12 max-w-md">
            <CodeEntry compact onUnlock={joinAndOpen} />
          </div>
        )}
      </div>
    );
  } else {
    content = (
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-16" data-testid="gallery-entry">
        <LoginForm onDone={() => {}} />
        <div className="md:border-l md:border-white/10 md:pl-16">
          <CodeEntry onUnlock={openByCode} />
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-24">
      <div className="w-full flex flex-col items-center">
        {user && (
          <div className="w-full max-w-6xl flex items-center justify-between gap-6 mb-12 -mt-6" data-testid="gallery-user-bar">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">
              Signed in as <span className="text-[#A3A3A3]">{user.email}</span>
            </p>
            <div className="flex items-center gap-6">
              {session && (
                <button onClick={() => setSession(null)} data-testid="gallery-back-btn"
                  className="text-[10px] tracking-[0.3em] uppercase text-[#C9A26B] hover:text-[#F5F4F0] transition-colors duration-300">
                  ← All weddings
                </button>
              )}
              <button onClick={handleLogout} data-testid="gallery-logout-btn"
                className="text-[10px] tracking-[0.3em] uppercase text-[#737373] hover:text-[#F5F4F0] border border-white/20 px-5 py-2.5 transition-colors duration-300">
                Log Out
              </button>
            </div>
          </div>
        )}
        {content}
      </div>
    </main>
  );
}
